import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorWarningCircle } from '../../../assets/icons';
import { useCharger } from '../../../hooks/useChargers';
import { useCompany } from '../../../hooks/useCompany';
import { useLicense } from '../../../hooks/useLicense';
import { useUnassignLicenseMutation } from '../../../services/license.api';
import { SCOPE_TYPE } from '../../../services/utils';
import { License } from '../../../stores/types';
import { UnassignLicenseRequestUrlParams } from '../../../stores/types/licenses.interface';
import {
  ButtonSize,
  ButtonType,
  ColorType,
  MODAL_TYPES,
  MenuItem,
  useGlobalModalContext,
} from '../../_ui';
import { LICENSE_STATUS } from '../utils';

interface UnassignLicenseMenuItemProps {
  license: License;
}

export const UnassignLicenseMenuItem: React.FC<UnassignLicenseMenuItemProps> = ({ license }) => {
  const { showModal } = useGlobalModalContext();
  const { t } = useTranslation();
  const { userScope } = useCompany();
  const { getChargerNameById } = useCharger();
  const { handleLoadPage } = useLicense();

  const handleUnassignLicense = useCallback(() => {
    const urlParams: UnassignLicenseRequestUrlParams = {
      scope: userScope || SCOPE_TYPE.COMPANY,
      companyId: license.companyId,
      licenseKey: license.licenseKey,
    };

    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('license_unassign_warn_title'),
      icon: ErrorWarningCircle,
      iconColor: ColorType.WARNING2,
      mutationHook: useUnassignLicenseMutation,
      mutationParams: {
        licensePatchRequestUrlParam: urlParams,
        licensePatchRequestToast: {
          licenseKey: license.licenseKey,
          chargerName: getChargerNameById(license.assignedCharger || ''),
        },
      },
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
        },
        {
          label: t('unassign'),
          type: ButtonType.PRIMARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
          isSubmit: true,
          dataTestId: 'unassign-license-button',
        },
      ],
      onClose: (qResult: any) => {
        if (qResult?.isSuccess) {
          handleLoadPage(1);
        }
      },
      shouldCloseOnOverlayClick: false,
    });
  }, [showModal, t, userScope, getChargerNameById, handleLoadPage, license]);

  const licenseStatusUnassigned = (row: License) => {
    return row.status?.toLowerCase() === LICENSE_STATUS.UNASSIGNED.toLowerCase();
  };

  const licenseStatusExpired = (row: License) => {
    return row.status?.toLowerCase() === LICENSE_STATUS.EXPIRED.toLowerCase();
  };

  // Only render if license is assigned, has a charger, and is not expired
  const shouldRender =
    !licenseStatusUnassigned(license) &&
    license.assignedCharger?.trim() &&
    !licenseStatusExpired(license);

  return shouldRender ? (
    <MenuItem
      label={t('license_unassign')}
      dataTestId='license_unassign'
      onClick={handleUnassignLicense}
    />
  ) : null;
};
